import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { auth as authApi } from '@/lib/api';
import DashboardLayout from '@/components/DashboardLayout';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Overview from '@/pages/Overview';
import Services from '@/pages/Services';
import ApiKeys from '@/pages/ApiKeys';
import Usage from '@/pages/Usage';
import Billing from '@/pages/Billing';
import Docs from '@/pages/Docs';
import Playground from '@/pages/Playground';
import Settings from '@/pages/Settings';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ConfigEditor from '@/pages/admin/ConfigEditor';
import UserManagement from '@/pages/admin/UserManagement';
import AuditLogs from '@/pages/admin/AuditLogs';
import GpuHealth from '@/pages/admin/GpuHealth';
import AdminLandingCMS from '@/pages/admin/AdminLandingCMS';
import AdminAnalytics from '@/pages/admin/Analytics';
import AdminReports from '@/pages/admin/Reports';
import GpuLogs from '@/pages/admin/GpuLogs';
import FailedRequests from '@/pages/admin/FailedRequests';
import ServerManagement from '@/pages/admin/ServerManagement';
import TierLimits from '@/pages/admin/TierLimits';
import CustomerAssignments from '@/pages/admin/CustomerAssignments';
import Landing from '@/pages/Landing';
import ServicesPage from '@/pages/ServicesPage';
import PricingPage from '@/pages/PricingPage';
import ResetPassword from '@/pages/ResetPassword';
import GettingStarted from '@/pages/GettingStarted';
import GettingStartedPublic from '@/pages/GettingStartedPublic';
import AgentGuide from '@/pages/AgentGuide';
import Status from '@/pages/Status';
import FeaturesIndex from '@/pages/FeaturesIndex';
import FeaturePage from '@/pages/FeaturePage';
import { landing as landingApi } from '@/lib/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const DEFAULT_THEME = { primary_color: '#6366f1', accent_color: '#7c3aed', button_radius: 'rounded', button_style: 'gradient' };
const RADIUS_MAP = { pill: '9999px', rounded: '12px', subtle: '6px', sharp: '0px' };

const BrandingContext = createContext({ brand_name: 'Tensornex', logo_url: '/tensornex-logo.png', favicon_url: '/tensornex-logo.png', page_title: 'Tensornex', theme: DEFAULT_THEME });
export const useBranding = () => useContext(BrandingContext);

function applyTheme(theme) {
  const t = { ...DEFAULT_THEME, ...theme };
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', t.primary_color);
  root.style.setProperty('--brand-accent', t.accent_color);
  root.style.setProperty('--brand-btn-radius', RADIUS_MAP[t.button_radius] || '12px');
}

function BrandingProvider({ children }) {
  const [branding, setBranding] = useState({ brand_name: 'Tensornex', logo_url: '/tensornex-logo.png', favicon_url: '/tensornex-logo.png', page_title: 'Tensornex | AI Infrastructure for Builders', theme: DEFAULT_THEME });

  useEffect(() => {
    landingApi.get().then(r => {
      const b = r.data?.branding;
      if (b) {
        setBranding(prev => ({ ...prev, ...b }));
        if (b.page_title) document.title = b.page_title;
        if (b.favicon_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = b.favicon_url;
        }
        if (b.theme) applyTheme(b.theme);
      }
    }).catch(() => {});
  }, []);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await authApi.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch full user data including enabled_services
    try {
      const res = await authApi.me();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await authApi.me();
      setUser(res.data);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<Overview />} />
            <Route path="getting-started" element={<GettingStarted />} />
            <Route path="services" element={<Services />} />
            <Route path="api-keys" element={<ApiKeys />} />
            <Route path="usage" element={<Usage />} />
            <Route path="billing" element={<Billing />} />
            <Route path="docs" element={<Docs />} />
            <Route path="playground" element={<Playground />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="/admin" element={<ProtectedRoute adminOnly><DashboardLayout isAdmin /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="config" element={<ConfigEditor />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="billing" element={<AdminDashboard />} />
            <Route path="logs" element={<AuditLogs />} />
            <Route path="system" element={<GpuHealth />} />
            <Route path="landing" element={<AdminLandingCMS />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="gpu-logs" element={<GpuLogs />} />
            <Route path="failed-requests" element={<FailedRequests />} />
            <Route path="servers" element={<ServerManagement />} />
            <Route path="tier-limits" element={<TierLimits />} />
            <Route path="customer-assignments" element={<CustomerAssignments />} />
          </Route>
          <Route path="/" element={<Landing />} />
          <Route path="/services-info" element={<ServicesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/features" element={<FeaturesIndex />} />
          <Route path="/features/:serviceId" element={<FeaturePage />} />
          <Route path="/getting-started" element={<GettingStartedPublic />} />
          <Route path="/getting-started/agent" element={<AgentGuide />} />
          <Route path="/status" element={<Status />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
